<template>
  <div class="home-container">
    <!-- 使用BannerSlider组件 -->
    <BannerSlider />

    <!-- Company Profile Section -->
    <section class="company-section">
      <div class="intro-container">
        <div class="intro-image">
          <img src="@/assets/images/n1.jpg" alt="中策橡胶集团总部" class="intro-img">
        </div>
        <div class="intro-content">
          <h2 class="intro-title">{{$t('home.brandIntroTitle')}}</h2>
          <p class="intro-text">{{$t('home.brandIntroText')}}</p>

          <h2 class="intro-title mt-40">{{$t('home.groupIntroTitle')}}</h2>
          <p class="intro-text">{{$t('home.groupIntroText1')}}</p>
          <p class="intro-text">{{$t('home.groupIntroText2')}}</p>

          <button class="learn-more-btn intro-btn" @click="$router.push('/about')">{{$t('common.learnMore')}} <span class="btn-arrow">→</span></button>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section">
      <div class="stats-container">
        <div class="stat-box">
          <div class="stat-year">{{ $t('stats.foundedYear') }}</div>
          <div class="stat-desc">{{ $t('stats.foundedDesc') }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-year">{{ $t('stats.revolutionYear') }}</div>
          <div class="stat-desc">{{ $t('stats.revolutionDesc') }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-year">{{ $t('stats.pioneersTitle') }}</div>
          <div class="stat-desc">{{ $t('stats.pioneersDesc') }}</div>
        </div>
        <div class="stat-box">
          <div class="stat-year">{{ $t('stats.standardTitle') }}</div>
          <div class="stat-desc">{{ $t('stats.standardDesc') }}</div>
        </div>
      </div>
    </section>

    <!-- Products Section -->
    <section class="products-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('products.title') }}</h2>
      </div>

      <div v-if="isProductsLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>{{$t('common.loadingProducts')}}</p>
      </div>

      <div v-else-if="productError" class="error-container">
        <p>{{ productError }}</p>
      </div>

      <div v-else-if="products.length === 0" class="empty-container">
        <p>{{$t('common.noProducts')}}</p>
      </div>

      <div v-else class="product-slider">
        <button v-if="products.length > 1" class="slider-arrow slider-prev outside" @click="prevProduct" :disabled="isProductsLoading">
          <span>&lt;</span>
        </button>

        <div class="product-card">
          <div class="product-image-container">
            <img
              :src="currentProduct.productImage ? getFullImageUrl(currentProduct.productImage) : require('@/assets/images/l1.png')"
              :alt="currentProduct.productName || 'Tire Product'"
              class="product-image"
            >
          </div>
          <div class="product-info">
            <h3 class="product-title">{{ currentProduct.productName}}</h3>
            <p class="product-subtitle" :style="{color: '#ed1c24'}">{{ currentProduct.productCarType}}</p>
            <p class="product-manufacturer">{{ currentProduct.productSellingPoints }}</p>
            <div class="product-detail">
              <a :href="currentProduct.jumpUrl || '#'" target="_blank" class="buy-now-btn">{{$t('common.buyNow')}}</a>
              <button class="text-btn details-btn" @click="goToProductDetail(currentProduct.id)">{{$t('common.learnMore')}} <span class="btn-arrow">→</span></button>
            </div>
          </div>
        </div>

        <button v-if="products.length > 1" class="slider-arrow slider-next outside" @click="nextProduct" :disabled="isProductsLoading">
          <span>&gt;</span>
        </button>
      </div>
    </section>

    <!-- Media Section -->
    <section class="media-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('media.title') }}</h2>
      </div>

      <div v-if="isMediaLoading" class="loading-container">
        <div class="loading-spinner"></div>
        <p>{{$t('common.loadingMedia')}}</p>
      </div>

      <div v-else-if="mediaError" class="error-container">
        <p>{{ mediaError }}</p>
      </div>

      <div v-else-if="mediaList.length === 0" class="empty-container">
        <p>{{$t('common.noMedia')}}</p>
      </div>

      <div v-else class="media-container">
        <!-- 使用API获取的媒体数据，仅展示前3条 -->
        <div class="media-article" v-for="(media, index) in mediaList.slice(0, 3)" :key="media.id || index">
          <div class="article-image">
            <img
              :src="media.imageUrl ? getFullImageUrl(media.imageUrl) : require(`@/assets/images/n${index+2}.png`)"
              :alt="media.title || 'Media Image'"
              class="article-img"
            >
          </div>
          <div class="article-content">
            <h3 class="article-title">{{ media.title }}</h3>
            <div class="article-date">{{ formatDate(media.createTime)}}</div>
            <p class="article-desc">{{ extractTextFromHtml(media.content) }}</p>
            <a href="#" class="article-link" v-if="currentLocale === 'zh'" @click.prevent="viewArticleDetail(media)">{{$t('common.learnMore')}} <span class="btn-arrow">→</span></a>
            <a href="#" class="article-link" v-else>{{$t('media.readMore')}} <span class="btn-arrow">→</span></a>
          </div>
        </div>
      </div>
    </section>

    <!-- Partners Section -->
    <section class="partners-section">
      <div class="section-header">
        <h2 class="section-title">{{ $t('partners.title') }}</h2>
        <p class="product-description-text">{{ $t('partners.description') }}</p>
      </div>
      <div class="partners-container">
        <!-- 使用循环显示前12个合作伙伴 -->
        <div class="partners-row" v-for="row in 2" :key="'row-'+row">
          <div class="partner-logo" v-for="i in 6" :key="i">
            <div class="partner-logo-inner">
              <img :src="require(`@/assets/images/c${(row-1)*6 + i}.${(row-1)*6 + i <= 2 ? 'png' : 'jpg'}`)" :alt="`Partner ${(row-1)*6 + i}`" class="partner-img">
            </div>
          </div>
        </div>

        <!-- 更多合作伙伴（默认隐藏） -->
        <div class="partners-more" v-if="showMorePartners">
          <!-- 使用循环显示c13-c30的图片 -->
          <div class="partners-row" v-for="row in 3" :key="'more-row-'+row">
            <div class="partner-logo" v-for="i in 6" :key="i">
              <div class="partner-logo-inner">
                <img :src="require(`@/assets/images/c${12 + (row-1)*6 + i}.jpg`)" :alt="`Partner ${12 + (row-1)*6 + i}`" class="partner-img">
              </div>
            </div>
          </div>
        </div>

        <!-- 查看更多按钮 -->
        <div class="view-more-container">
          <button class="view-more-btn" @click="toggleMorePartners">
            {{ showMorePartners ? $t('common.collapse') : $t('common.viewMore') }}
          </button>
        </div>
      </div>
    </section>

    <!-- Footer Section -->
    <SiteFooter />
  </div>
</template>

<script>
// 导入BannerSlider组件
import BannerSlider from '@/components/BannerSlider.vue';
import SiteFooter from '@/components/SiteFooter.vue';

export default {
  name: 'HomePage',
  // 注册组件
  components: {
    BannerSlider,
    SiteFooter
  },
  data() {
    return {
      currentLocale: this.$i18n.locale || 'zh', // 默认中文
      currentProductIndex: 0,
      showMorePartners: false, // 是否显示更多合作伙伴
      products: [],
      isProductsLoading: false,
      productError: null,
      mediaList: [],
      isMediaLoading: false,
      mediaError: null,
      partners: [
        { name: 'Audi', logo: '@/assets/images/c1.png' },
        { name: 'BMW', logo: '@/assets/images/c2.png' },
        { name: 'Ford', logo: '@/assets/images/c3.png'},
        { name: 'Mercedes Benz', logo: '@/assets/images/c4.png' },
        { name: 'Peugeot', logo: '@/assets/images/c5.png' },
        { name: 'Volkswagen', logo: '@/assets/images/c6.png' },
        { name: 'Toyota', logo: '@/assets/images/c7.png' },
        { name: 'Honda', logo: '@/assets/images/c8.png' },
        { name: 'Nissan', logo: '@/assets/images/c9.png' },
        { name: 'Hyundai', logo: '@/assets/images/c10.png' },
        { name: 'Kia', logo: '@/assets/images/c11.png' },
        { name: 'Mazda', logo: '@/assets/images/c12.png' }
      ],
    };
  },
  computed: {
    currentProduct() {
      return this.products.length > 0 ? this.products[this.currentProductIndex] : {};
    }
  },
  created() {
    // 从 localStorage 初始化语言
    const savedLocale = localStorage.getItem('locale');
    if (savedLocale) {
      this.currentLocale = savedLocale;
      this.$i18n.locale = savedLocale;
    }

    // 监听语言变化
    this.$root.$on('language-changed', (locale) => {
      this.currentLocale = locale;
    });

    // 获取产品列表
    this.loadHomeProducts();

    // 获取媒体列表
    this.loadHomeMedia();
  },
  methods: {
    // 获取产品完整图片URL
    getFullImageUrl(imageUrl) {
      if (!imageUrl) return '';
      if (imageUrl.startsWith('http')) return imageUrl;
      return process.env.VUE_APP_IMAGE_URL + imageUrl;
    },

    // 从HTML中提取纯文本并限制长度
    extractTextFromHtml(html) {
      if (!html) return '';
      // 创建临时DOM元素来解析HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      // 获取纯文本内容
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      // 限制文本长度 (约6行，每行约80个字符)
      return textContent.length > 480 ? textContent.substring(0, 480) + '...' : textContent;
    },

    // 加载首页产品列表
    loadHomeProducts() {
      this.isProductsLoading = true;
      this.productError = null;

      this.$api.home.getHomeProducts({
        pageNum: 1,
        pageSize: 10,
        publishLocation:2
      })
        .then(response => {
          console.log('首页产品数据:', response);
          if (response.rows && response.rows.length > 0) {
            this.products = response.rows;
            this.currentProductIndex = 0;
          } else {
            this.products = [];
          }
        })
        .catch(error => {
          console.error('获取产品失败:', error);
          this.productError = '加载产品数据失败，请稍后再试';
        })
        .finally(() => {
          this.isProductsLoading = false;
        });
    },

    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return '';
      // 只保留日期部分，不显示时间
      return dateString.split(' ')[0];
    },

    // 加载首页媒体列表
    loadHomeMedia() {
      this.isMediaLoading = true;
      this.mediaError = null;

      this.$api.media.getNewsListNew({
        pageNum: 1,
        pageSize: 3 // 只获取3条
      })
        .then(response => {
          console.log('首页媒体数据:', response);
          if (response.rows && response.rows.length > 0) {
            this.mediaList = response.rows;
          } else {
            this.mediaList = [];
          }
        })
        .catch(error => {
          console.error('获取媒体信息失败:', error);
          this.mediaError = '加载媒体数据失败，请稍后再试';
        })
        .finally(() => {
          this.isMediaLoading = false;
        });
    },

    changeLanguage(locale) {
      this.currentLocale = locale;
      this.$i18n.locale = locale;
      localStorage.setItem('locale', locale);
    },

    prevProduct() {
      if (this.currentProductIndex > 0) {
        this.currentProductIndex--;
      } else {
        this.currentProductIndex = this.products.length - 1;
      }
    },

    nextProduct() {
      if (this.currentProductIndex < this.products.length - 1) {
        this.currentProductIndex++;
      } else {
        this.currentProductIndex = 0;
      }
    },

    goToProductDetail(productId) {
      if (productId) {
        // 导航到产品详情页面
        this.$router.push(`/product/${productId}`);
      } else {
        // 如果没有ID，导航到产品页面
        this.$router.push('/products');
      }
    },

    toggleMorePartners() {
      this.showMorePartners = !this.showMorePartners;
    },

    viewArticleDetail(article) {
      if (article.id) {
        this.$router.push(`/blog/${article.id}`);
      }
    }
  }
};
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #ed1c24; // BullTire Red
$secondary-color: #222; // Dark Grey/Black
$light-color: #fff;
$gray-color: #f5f5f5;
$dark-text: #212529;
$light-text: #fff;
$border-radius: 4px;

// Breakpoints
$mobile: 767px;
$tablet: 1024px;

// Section padding
$section-padding: 80px 0;

/* Global styles */
.home-container {
  width: 100%;
  overflow-x: hidden;
  position: relative;
}

.section-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 20px;
  color: $dark-text;
}

.section-subtitle {
  color: $primary-color;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 10px;
}

/* Button styles */
.btn-primary {
  background-color: $primary-color;
  color: $light-text;
  border: none;
  padding: 12px 30px;
  border-radius: 30px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  font-size: 14px;

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

.btn-secondary {
  background-color: $primary-color;
  color: $light-text;
  border: none;
  padding: 10px 25px;
  border-radius: 30px;
  font-weight: 500;
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;

  &:after {
    content: '→';
    margin-left: 10px;
  }

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

/* Hero Section */
.hero-section {
  position: relative;
  color: $light-text;
  overflow: hidden;
  width: 100%;
  height: 100vh; /* 填满整个视口高度 */
  min-height: 600px; /* 最小高度以确保在小屏幕上显示正常 */


  @media (max-width: $mobile) {
    height: auto;
    min-height: 300px;
  }
}

.hero-media {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.hero-video,
.hero-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.hero-overlay {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  z-index: 2;
  padding: 0 10%;

  /* 移除背景遮罩 */
  background-color: transparent;

  &.content-only {
    /* 如果你需要为文本添加一点点阴影效果以保证可读性 */
    text-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
  }
}

.hero-controls {
  position: absolute;
  bottom: 70px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
}

.banner-indicators {
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 12px;
  z-index: 10;

  @media (max-width: $mobile) {
    bottom: 20px;
  }
}

.banner-line {
  width: 30px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s;
  cursor: pointer;

  &.active {
    background-color: $primary-color;
    width: 40px;
  }
}

.banner-arrows {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  z-index: 10;
}

.banner-arrow {
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: $primary-color;
  }
}

.hero-content {
  max-width: 600px;
  position: relative;
  z-index: 2;

  /* 为文本内容添加阴影效果以保证在明亮背景上的可读性 */
  .hero-title, .hero-subtitle, .hero-years, .hero-fans {
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  }

  &.simple-content {
    text-align: left;
    max-width: 500px;

    .hero-title {
      font-size: 50px;
      margin-bottom: 15px;
    }

    .hero-subtitle {
      font-size: 22px;
      line-height: 1.4;
      opacity: 0.9;
    }
  }
}

.hero-subtitle {
  color: $light-text;
  font-weight: 500;
  margin-bottom: 15px;
  font-size: 18px;
}

.hero-title {
  font-size: 42px;
  font-weight: bold;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: $mobile) {
    font-size: 32px;
  }
}

.hero-years, .hero-fans {
  font-size: 18px;
  margin-bottom: 10px;
  opacity: 0.9;
}

.hero-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 30px;

  .badge {
    background-color: rgba(255,255,255,0.2);
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 14px;
  }
}

/* Company Section */
.company-section {
  padding: 0;
  background-color: #f9f9f9;
  margin-top: 60px;
}

.company-container {
  display: flex;
  max-width: 100%;
  margin: 0;
  gap: 0;

  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.company-image {
  flex: 1;
  height: auto;
  min-height: 450px;
  background-color: #ddd;
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;

  .company-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: $tablet) {
    height: 300px;
  }
}

.company-content {
  flex: 1;
  padding: 60px;
  background-color: #f9f9f9;
}

.company-text {
  margin-bottom: 20px;
  line-height: 1.6;
  color: #666;
  font-size: 16px;
}

/* Stats Section */
.stats-section {
  padding: 80px 0;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  @media (max-width: $tablet) {
    padding: 20px 0;
  }
}

.stats-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;

  @media (max-width: $tablet) {
    flex-wrap: wrap;
    gap: 30px;
  }

  @media (max-width: $mobile) {
    flex-direction: column;
    gap: 20px;
  }
}

.stat-box {
  flex: 1;
  text-align: left;
  padding: 0 20px;

  @media (max-width: $tablet) {
    width: calc(50% - 15px);
  }

  @media (max-width: $mobile) {
    width: 100%;
    margin-bottom: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    padding: 0;
  }
}

.stat-year {
  font-size: 42px;
  font-weight: bold;
  margin-bottom: 10px;
  color: $dark-text;

  @media (max-width: $mobile) {
    font-size: 28px;
    margin-bottom: 0;
    flex-shrink: 0;
    width: 120px;
    font-weight: 700;
    margin-left: 40px;
  }
}

.stat-desc {
  font-size: 15px;
  color: #666;
  line-height: 1.4;
  max-width: 300px;

  @media (max-width: $mobile) {
    font-size: 14px;
    line-height: 1.5;
    text-align: left;
    flex: 1;
    margin-left: 20px;
    color: #666;
  }
}

/* Brand Introduction Section */
.brand-intro-section {
  padding: $section-padding;
  background-color: #fff;
}

.intro-container {
  display: flex;
  margin: 0 auto;
  gap: 0;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);

  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.intro-image {
  flex: 0 0 45%;
  height: auto;
  min-height: 500px;
  background-color: #f5f5f5;
  position: relative;
  overflow: hidden;

  .intro-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media (max-width: $tablet) {
    height: 300px;
    flex: 0 0 auto;
  }

  @media (max-width: $mobile) {
    min-height: auto;
  }
}

.intro-content {
  flex: 1;
  padding: 60px;
  background-color: #fff;
}

.intro-title {
  font-size: 32px;
  font-weight: bold;
  margin-bottom: 20px;
  color: $dark-text;
  position: relative;
}

.intro-text {
  margin-bottom: 20px;
  line-height: 1.8;
  color: #666;
  font-size: 16px;
}

.intro-btn {
  background-color: $primary-color;
  color: $light-text;
  border: none;
  padding: 10px 25px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;

  .btn-arrow {
    margin-left: 5px;
  }

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

/* Products Section */
.products-section {
  padding: $section-padding;
  background-color: #f9f9f9;
  text-align: center;
}

.product-section-subtitle {
  display: inline-block;
  padding: 3px 10px;
  background-color: rgba(237, 28, 36, 0.1);
  color: $primary-color;
  border-radius: 3px;
  margin-bottom: 10px;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.product-slider {
  position: relative;
  max-width: 1200px;
  width: 90%;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 40px;

  @media (max-width: $mobile) {
    padding: 0
  }
  
}

.slider-arrow {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #fff;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  box-shadow: 0 2px 5px rgba(0,0,0,0.1);
  transition: all 0.3s;
  font-size: 20px;
  color: #666;

  &:hover {
    background-color: #f5f5f5;
  }

  &.outside {
    background-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
}

.slider-prev {
  left: 10px;

  &.outside {
    left: -20px;
  }
}

.slider-next {
  right: 10px;

  &.outside {
    right: -20px;
  }
}

@media (max-width: $mobile) {
  .product-slider {
    flex-direction: column;
  }

  .slider-arrow {
    position: static;
    margin: 20px 10px;
    transform: none;
  }

  .product-card {
    order: 2;
  }

  .slider-prev {
    order: 1;
  }

  .slider-next {
    order: 3;
  }
}

.product-card {
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  background-color: $light-color;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  .product-image-container {
    width: 100%;
  flex: 0 0 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-color;

}
  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.product-image-container {
  flex: 0 0 45%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: $light-color;
  width: 400px;
  height: 400px;
}

.product-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  display: block;
}

.product-info {
  flex: 1;
  padding: 50px;
  background-color: $light-color;
  text-align: left;
  display: flex;
  flex-direction: column;

  @media (max-width: $mobile) {
    padding: 25px;
  }
}

.product-title {
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #121212;
}

.product-subtitle {
  font-size: 22px;
  font-weight: bold;
  color: #E00000;
  margin-bottom: 25px;
}

.product-manufacturer {
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-bottom: 30px;
}

.product-description {
  font-size: 15px;
  color: #666;
  margin-bottom: 30px;
  line-height: 1.6;
}

.product-detail {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 30px;
}

.buy-now-btn {
  background-color: #d81e06;
  color: #fff;
  border: none;
  padding: 10px 25px;
  border-radius: 30px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  text-decoration: none;
  width: 140px;

  &:hover {
    background-color: darken(#d81e06, 10%);
  }
}

.text-btn {
  background: none;
  border: none;
  color: #666;
  font-weight: 500;
  font-size: 14px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.3s;

  .btn-arrow {
    margin-left: 5px;
    transition: transform 0.3s;
  }

  &:hover {
    color: $primary-color;

    .btn-arrow {
      transform: translateX(3px);
    }
  }
}

.details-btn {
  color: #666;
}

.product-description-text {
  max-width: 700px;
  margin: 0 auto 50px;
  color: #666;
  font-size: 16px;
  line-height: 1.5;
}

/* Media Section */
.media-section {
  padding: $section-padding;
  background-color: #fff;
}

.media-container {
  display: flex;
  flex-direction: column;
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
}

.media-article {
  display: flex;
  background-color: $light-color;
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  border: none;

  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.article-image {
  flex: 0 0 35%;
  overflow: hidden;
  position: relative;
  min-width: 300px;

  .article-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
    min-height: 200px;
  }

  &.image-2 {
    background-position: left center;
  }

  @media (max-width: $tablet) {
    width: 100%;
    height: 200px;
  }
}

.article-content {
  flex: 1;
  padding: 30px;
  display: flex;
  flex-direction: column;
}

.article-title {
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #121212;
}

.article-date {
  font-size: 14px;
  color: #999;
  margin-bottom: 12px;
}

.article-desc {
  font-size: 15px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.6;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 6;
  -webkit-box-orient: vertical;
  max-height: 9.6em; /* 6 lines × 1.6 line-height */
}

.article-link {
  color: $primary-color;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  margin-top: auto;
  width: fit-content;

  .btn-arrow {
    margin-left: 5px;
  }

  &:hover {
    text-decoration: underline;
  }
}

/* Partners Section */
.partners-section {
  padding: $section-padding;
  text-align: center;
  background-color: #f9f9f9;
}

.partners-container {
  max-width: 1200px;
  margin: 0 auto;
}

.partners-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: $mobile) {
    gap: 10px;
  }
}

.partner-logo {
  flex: 0 0 calc(16.666% - 20px);
  background-color: $light-color;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 180px;
  height: 100px;

  @media (max-width: $tablet) {
    flex: 0 0 calc(33.333% - 20px);
  }

  @media (max-width: $mobile) {
    flex: 0 0 calc(50% - 10px);
  }
}

.partner-logo-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 15px;
  width: 100%;
  height: 100%;

  .partner-img {
    width: auto;
    height: auto;
    max-width: 150px;
    max-height: 80px;
    object-fit: contain;
  }
}

.partner-name {
  font-size: 18px;
  font-weight: 500;
  color: #333;
  margin-top: 10px;
  display: none; /* 隐藏名称，只显示图标 */
}

/* Footer Styles */
.site-footer {
  width: 100%;
  position: relative;
}

.footer-main {
  background-color: #333;
  color: #fff;
  padding: 60px 0;
}

.footer-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;

  @media (max-width: $tablet) {
    flex-direction: column;
  }
}

.footer-left {
  width: 30%;
  padding-right: 40px;

  @media (max-width: $tablet) {
    width: 100%;
    padding-right: 0;
    margin-bottom: 40px;
  }
}

.footer-logo {
  margin-bottom: 20px;
}

.footer-logo-image {
  max-width: 120px;
}

.footer-description {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  line-height: 1.6;
}

.footer-columns {
  display: flex;
  flex: 1;
  justify-content: space-between;

  @media (max-width: $mobile) {
    flex-direction: column;
    gap: 30px;
  }
}

.footer-column {
  padding: 0 15px;

  @media (max-width: $mobile) {
    padding: 0;
  }
}

.footer-title {
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 20px;
  color: #fff;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 10px;

    a {
      color: rgba(255, 255, 255, 0.7);
      text-decoration: none;
      font-size: 14px;

      &:hover {
        color: #fff;
      }
    }
  }
}

.qr-codes {
  display: flex;
  gap: 20px;

  @media (max-width: $mobile) {
    gap: 15px;
  }
}

.qr-code {
  text-align: center;

  span {
    display: block;
    margin-bottom: 10px;
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
  }
}

.qr-image {
  width: 120px;
  height: 120px;
  background-color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: $mobile) {
    width: 100px;
    height: 100px;
  }
}

.footer-bottom {
  background-color: #ed1c24;
  color: #fff;
  padding: 15px 0;

  .footer-container {
    display: block;
    text-align: center;
  }
}

.copyright {
  font-size: 14px;
  margin: 0;
  color: rgba(255, 255, 255, 0.9);
}

.mt-40 {
  margin-top: 40px;
}

.view-more-container {
  text-align: center;
  margin-top: 30px;
}

.view-more-btn {
  background-color: $primary-color;
  color: #fff;
  border: none;
  padding: 10px 30px;
  border-radius: 30px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s ease;

  &:hover {
    background-color: darken($primary-color, 10%);
  }
}

.partners-more {
  margin-top: 20px;
  animation: fadeIn 0.5s ease;
}

.invisible {
  visibility: hidden;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Loading and error styles */
.loading-container, .error-container, .empty-container {
  text-align: center;
  padding: 40px;
  color: #666;
}

.loading-spinner {
  display: inline-block;
  width: 40px;
  height: 40px;
  border: 4px solid rgba(237, 28, 36, 0.2);
  border-radius: 50%;
  border-top-color: $primary-color;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
